/**
 * Analytics Provider for visitor and session tracking.
 *
 * Features:
 * - Generates and persists visitorId in localStorage
 * - Generates sessionId on mount, refreshes after 30min inactivity
 * - Tracks page views on route change
 * - Fire-and-forget event sending to backend
 */

/* eslint-disable react-refresh/only-export-components, react-hooks/refs */

import { createContext, useContext, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

// Session timeout in milliseconds (30 minutes)
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

// localStorage keys
const VISITOR_ID_KEY = 'kyykka_visitor_id';
const SESSION_ID_KEY = 'kyykka_session_id';
const LAST_ACTIVITY_KEY = 'kyykka_last_activity';

// API endpoint
const ANALYTICS_API_URL = '/api/analytics/event';

/**
 * Generate a UUID v4.
 */
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get or create visitor ID from localStorage.
 */
function getVisitorId(): string {
  let visitorId = localStorage.getItem(VISITOR_ID_KEY);
  if (!visitorId) {
    visitorId = generateUUID();
    localStorage.setItem(VISITOR_ID_KEY, visitorId);
  }
  return visitorId;
}

/**
 * Get or create session ID, considering timeout.
 */
function getSessionId(): string {
  const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
  const storedSessionId = localStorage.getItem(SESSION_ID_KEY);

  // Check if session has expired
  if (lastActivity && storedSessionId) {
    const lastActivityTime = parseInt(lastActivity, 10);
    if (Date.now() - lastActivityTime < SESSION_TIMEOUT_MS) {
      // Session still valid
      return storedSessionId;
    }
  }

  // Create new session
  const newSessionId = generateUUID();
  localStorage.setItem(SESSION_ID_KEY, newSessionId);
  return newSessionId;
}

/**
 * Update last activity timestamp.
 */
function updateLastActivity(): void {
  localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
}

export interface AnalyticsContextValue {
  /** Current visitor ID */
  visitorId: string;
  /** Current session ID */
  sessionId: string;
  /** Track a custom event */
  trackEvent: (eventName: string, metadata?: Record<string, unknown>) => void;
  /** Track a page view (called automatically on route change) */
  trackPageView: (path: string, referrer?: string) => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

interface AnalyticsProviderProps {
  children: React.ReactNode;
  /** Disable tracking (useful for development/testing) */
  disabled?: boolean;
}

export function AnalyticsProvider({ children, disabled = false }: AnalyticsProviderProps) {
  const location = useLocation();
  const previousPath = useRef<string>('');
  const visitorIdRef = useRef<string>('');
  const sessionIdRef = useRef<string>('');

  // Initialize IDs on mount
  useEffect(() => {
    if (disabled) return;

    visitorIdRef.current = getVisitorId();
    sessionIdRef.current = getSessionId();
    updateLastActivity();
  }, [disabled]);

  /**
   * Send event to backend (fire-and-forget).
   */
  const sendEvent = useCallback(
    async (eventData: Record<string, unknown>) => {
      if (disabled) return;

      try {
        // Refresh session ID if needed
        sessionIdRef.current = getSessionId();
        updateLastActivity();

        const payload = {
          session_id: sessionIdRef.current,
          visitor_id: visitorIdRef.current,
          timestamp: new Date().toISOString(),
          user_agent: navigator.userAgent,
          ...eventData,
        };

        // Fire and forget - don't await
        fetch(ANALYTICS_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
          // Use keepalive to ensure request completes even if page unloads
          keepalive: true,
        }).catch(() => {
          // Silently ignore errors - analytics should never break the app
        });
      } catch {
        // Silently ignore errors
      }
    },
    [disabled]
  );

  /**
   * Track a page view.
   */
  const trackPageView = useCallback(
    (path: string, referrer?: string) => {
      sendEvent({
        page_path: path,
        referrer: referrer || '',
        metadata: {
          screen_width: window.innerWidth,
          screen_height: window.innerHeight,
        },
      });
    },
    [sendEvent]
  );

  /**
   * Track a custom event.
   */
  const trackEvent = useCallback(
    (eventName: string, metadata?: Record<string, unknown>) => {
      sendEvent({
        page_path: location.pathname,
        referrer: '',
        metadata: {
          event_name: eventName,
          ...metadata,
        },
      });
    },
    [sendEvent, location.pathname]
  );

  // Track page views on route change
  useEffect(() => {
    if (disabled) return;

    const currentPath = location.pathname + location.search;

    // Don't track if path hasn't changed
    if (currentPath === previousPath.current) return;

    // Track page view with previous path as referrer
    trackPageView(currentPath, previousPath.current);

    // Update previous path
    previousPath.current = currentPath;
  }, [location.pathname, location.search, trackPageView, disabled]);

  const value = useMemo<AnalyticsContextValue>(
    () => ({
      visitorId: visitorIdRef.current,
      sessionId: sessionIdRef.current,
      trackEvent,
      trackPageView,
    }),
    [trackEvent, trackPageView]
  );

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}

/**
 * Hook to access analytics context.
 */
export function useAnalytics(): AnalyticsContextValue {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
}

